/**
 * Polarity badge for Evidence v0.2 (CONTRACT_V2 §9).
 *
 * Renders the evidence `polarity` enum (supports / contradicts / qualifies /
 * weakens) as a black/neutral pill — never a coloured status badge. Per the
 * design system (§1.2, §5.5) black is the only accent: the affirmative stance
 * (`supports`) carries the filled black `default` badge, every other stance is
 * a calm `secondary` chip. A small directional glyph (from the icon module)
 * keeps the four stances legible without leaning on colour.
 *
 * Kept local to the evidence resource so these views stay self-contained and do
 * not depend on the (separately-owned) shared `app/PolarityBadge` component.
 */
import type { EvidencePolarity } from "@aos/shared";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Icon, type IconName } from "@/components/ui/icon";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

/** Black-only register: filled black for the affirmative stance, neutral otherwise. */
const POLARITY_VARIANT: Record<EvidencePolarity, BadgeVariant> = {
  supports: "default",
  contradicts: "secondary",
  qualifies: "secondary",
  weakens: "secondary",
};

const POLARITY_LABEL: Record<EvidencePolarity, string> = {
  supports: "Supports",
  contradicts: "Contradicts",
  qualifies: "Qualifies",
  weakens: "Weakens",
};

const POLARITY_ICON: Record<EvidencePolarity, IconName> = {
  supports: "approve",
  contradicts: "reject",
  qualifies: "scope",
  weakens: "prev",
};

export function PolarityBadge({
  polarity,
}: {
  polarity: EvidencePolarity | null | undefined;
}) {
  if (!polarity) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge variant={POLARITY_VARIANT[polarity]} className="gap-1.5">
      <Icon name={POLARITY_ICON[polarity]} size={12} aria-hidden />
      {POLARITY_LABEL[polarity]}
    </Badge>
  );
}

export default PolarityBadge;
